import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDropzone } from "react-dropzone"
import { getTintVTOService } from "services/tintvto"
import { downloadFileFromUrl } from "utils/fileReader"
import { fileToBase64 } from "utils/logicWithFiles"
import { Button } from "@/components/ui/button"
import { customDefaultHeaders, type ProductFields } from "."
import { useQueryClient } from "@tanstack/react-query"
import { TrashIcon } from "lucide-react"
import { useAppSelector } from "@/hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"

interface UploadBulkProductsProps {
  onAlert: (data: { error: boolean; text: string | React.ReactNode }) => void
  backHandler: () => void
  onClose: () => void
  hideBack?: boolean
}

interface UploadBulkError {
  graphQLErrors?: Array<{ errorInfo?: string }>
  networkError?: unknown
}

const MERCHANT_HEADER = "Merchant"

const quoteCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`

const appendMerchantColumn = (content: string, merchantId: string) => {
  const normalizedMerchantId = merchantId.trim()
  if (!normalizedMerchantId) return content

  const lineEnding = content.match(/\r\n|\n|\r/)?.[0] ?? "\n"
  const endsWithLineEnding = /(?:\r\n|\n|\r)$/.test(content)
  const lines = content.split(/\r\n|\n|\r/)
  if (endsWithLineEnding) lines.pop()
  if (lines.length === 0) return content

  const nextLines = lines.map((line, index) => {
    if (index === 0) return `${line},${quoteCsvValue(MERCHANT_HEADER)}`
    if (!line.trim()) return line

    return `${line},${quoteCsvValue(normalizedMerchantId)}`
  })

  return `${nextLines.join(lineEnding)}${endsWithLineEnding ? lineEnding : ""}`
}

export const UploadBulkProducts: React.FC<UploadBulkProductsProps> = ({
  onAlert,
  backHandler,
  onClose,
  hideBack = false,
}) => {
  const { t } = useTranslation()
  const [csvHeadersWithKeys, setCsvHeadersWithKeys] = useState<
    { [key in ProductFields]?: string } | null
  >(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvContent, setCsvContent] = useState<string | null>(null)
  const [isCsvValid, setIsCsvValid] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [headerValidationErrors, setHeaderValidationErrors] = useState<string[]>([])
  const [backendErrors, setBackendErrors] = useState<string | Record<string, unknown> | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const service = getTintVTOService()
  const queryClient = useQueryClient()
  const userInfo = useAppSelector(selectUserInfo)
  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")

  const resetSelectedCsv = () => {
    setCsvFile(null)
    setCsvContent(null)
    setCsvHeadersWithKeys(null)
    setIsCsvValid(false)
    setHeaderValidationErrors([])
    setBackendErrors(null)
  }

  const validateHeaders = (fileHeaders: string[]) => {
    const newHeadersWithKeys: { [key in ProductFields]?: string } = {}
    const errors: string[] = []
    Object.keys(customDefaultHeaders).forEach((key) => {
      const field = key as ProductFields
      const headerTemplate = customDefaultHeaders[field]
      if (!headerTemplate) return

      const { key: expectedHeader, required } = headerTemplate
      const headerIndex = fileHeaders.findIndex(
        (header) => header.trim().toLowerCase() === expectedHeader.trim().toLowerCase()
      )
      if (headerIndex !== -1) newHeadersWithKeys[field] = fileHeaders[headerIndex]
      else if (required)
        errors.push(t("uploadBulkProducts.expectedHeaderIsMissing", { expectedHeader }))
    })

    setIsCsvValid(errors.length === 0)
    setHeaderValidationErrors(errors)
    setCsvHeadersWithKeys(errors.length === 0 ? newHeadersWithKeys : null)
  }

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return

    setBackendErrors(null)
    setHeaderValidationErrors([])
    setCsvHeadersWithKeys(null)
    setIsCsvValid(false)
    setCsvContent(null)

    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = () => {
      const content = reader.result?.toString()
      if (!content) return

      setCsvContent(content)
      const rows = content
        .split("\n")
        .map((line) => line.split(",").map((header) => header.trim().replace(/^"|"$/g, "")))
      const newHeaders = rows[0]
      setCsvFile(file)
      validateHeaders(newHeaders)
    }
    reader.onerror = () => {
      onAlert({
        error: true,
        text: `${t("uploadBulkProducts.csvError")} ${reader.error?.message}`,
      })
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  })

  const handleUpload = async () => {
    if (!csvFile || !csvHeadersWithKeys || isUploading) return

    setIsUploading(true)

    try {
      const uploadFile =
        csvContent && !isAdmin
          ? new Blob([appendMerchantColumn(csvContent, userInfo.merchantId)], {
              type: csvFile.type || "text/csv",
            })
          : csvFile
      const fileDataUrl = await fileToBase64(uploadFile)
      const baseFile = fileDataUrl.includes(",") ? fileDataUrl.split(",")[1] : fileDataUrl
      const response = await service.uploadBulk(
        baseFile,
        window.btoa(JSON.stringify(csvHeadersWithKeys))
      )
      const data = response?.data
      onAlert({
        error: !data.uploadBulkNative,
        text: t("uploadBulkProducts.csvUploadSuccess"),
      })
      queryClient.invalidateQueries({ queryKey: ["products"] })
      onClose()
    } catch (error: unknown) {
      const uploadError = error as UploadBulkError
      if (uploadError.graphQLErrors && uploadError.graphQLErrors.length > 0) {
        const graphQlErrorInfo = uploadError.graphQLErrors[0]?.errorInfo
        const errorMessage = graphQlErrorInfo ? JSON.parse(graphQlErrorInfo) : {}
        setBackendErrors(errorMessage)
      } else if (uploadError.networkError) {
        setBackendErrors(t("uploadBulkProducts.networkError"))
      } else {
        setBackendErrors(t("uploadBulkProducts.uploadError"))
      }
    } finally {
      setIsUploading(false)
    }
  }

  const downloadExampleCsvHandler = async () => {
    setIsDownloading(true)
    if (isAdmin)
      await downloadFileFromUrl("/assets/bulk_sample_admin.csv", "Admin_Custom_Sample_Sheet.csv")
    else await downloadFileFromUrl("/assets/bulk_sample.csv", "Custom_Sample_Sheet.csv")

    setTimeout(() => setIsDownloading(false), 2000)
  }

  const hasFileError = Boolean(backendErrors) || headerValidationErrors.length > 0

  return (
    <div className="flex flex-col gap-4" data-testid="bulk-upload">
      <p className="flex flex-wrap items-center justify-center gap-x-1 text-center text-sm text-muted-foreground">
        <button
          type="button"
          data-testid="bulk-upload-download-template"
          className="inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-2 hover:text-foreground/80 disabled:opacity-60"
          onClick={!isDownloading ? downloadExampleCsvHandler : undefined}
          disabled={isDownloading}
        >
          <img src="/download.svg" alt="" className="h-3.5 w-3.5" />
          {isDownloading
            ? t("uploadBulkProducts.downloading")
            : t("uploadBulkProducts.downloadTheTemplate")}
        </button>
        <span>{t("uploadBulkProducts.toRepeatCorrectFormat")}</span>
      </p>

      <div
        {...getRootProps()}
        data-testid="bulk-upload-dropzone"
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          isDragActive ? "border-vto-accent bg-vto-accent-soft" : "border-border hover:bg-muted/50"
        }`}
      >
        <input {...getInputProps()} data-testid="bulk-upload-file-input" />
        <p className="text-sm text-muted-foreground">
          {isDragActive
            ? t("uploadBulkProducts.dropFileHere")
            : csvFile
            ? t("uploadBulkProducts.dragAndDropOverwrite")
            : t("uploadBulkProducts.dragAndDropSelect")}
        </p>
      </div>

      {csvFile && (
        <div
          data-testid="bulk-upload-selected-file"
          className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
            hasFileError ? "border-destructive/60" : "border-border"
          }`}
        >
          <span className="truncate text-sm font-medium">{csvFile.name}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={resetSelectedCsv}
            data-testid="bulk-upload-remove-file"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      )}

      {headerValidationErrors.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="bulk-upload-header-errors">
          {headerValidationErrors.map((error, index) => (
            <p key={index} className="text-xs text-destructive">
              {error}
            </p>
          ))}
        </div>
      )}

      {backendErrors && (
        <p className="text-sm text-destructive" data-testid="bulk-upload-backend-error">
          <span>{t("uploadBulkProducts.somethingIsWrong")}</span>
          <button
            type="button"
            className="cursor-pointer underline underline-offset-2"
            onClick={() => {
              const content =
                typeof backendErrors === "object"
                  ? JSON.stringify(backendErrors, null, 2)
                  : backendErrors

              const blob = new Blob([content], { type: "text/plain" })
              const url = window.URL.createObjectURL(blob)
              const link = document.createElement("a")
              link.href = url
              link.download = "error.txt"
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              window.URL.revokeObjectURL(url)
            }}
          >
            {t("uploadBulkProducts.downloadError")}
          </button>
        </p>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        {hideBack ? (
          <span />
        ) : (
          <Button onClick={backHandler} variant="ghost" size="sm" data-testid="bulk-upload-back">
            {t("uploadBulkProducts.back")}
          </Button>
        )}
        <Button
          onClick={handleUpload}
          disabled={!csvFile || !isCsvValid || isUploading}
          size="sm"
          data-testid="bulk-upload-submit"
        >
          {isUploading ? t("uploadBulkProducts.uploading") : t("uploadBulkProducts.uploadBulk")}
        </Button>
      </div>
    </div>
  )
}
